/**
 * Central app configuration. Every tunable lives here (or, for scoring rules,
 * in `formation_rules_jsonb` whose defaults are `@scoring/rules`).
 * No magic values in screens.
 */
import * as Localization from 'expo-localization';

/** Competitions the app surfaces, by API-Football league id. */
export const SURFACED_COMPETITIONS = [
  { providerId: 1, fantasy: true, key: 'worldCup' }, // FIFA World Cup
  { providerId: 2, fantasy: false, key: 'championsLeague' }, // UEFA Champions League
  { providerId: 39, fantasy: true, key: 'premierLeague' }, // Premier League
  { providerId: 140, fantasy: true, key: 'laLiga' }, // La Liga
  { providerId: 135, fantasy: false, key: 'serieA' }, // Serie A
  { providerId: 78, fantasy: false, key: 'bundesliga' }, // Bundesliga
  { providerId: 61, fantasy: false, key: 'ligue1' }, // Ligue 1
] as const;

export const QUERY_STALE_TIME = {
  /** Reference data refreshed nightly server-side. */
  reference: 1000 * 60 * 30,
  /** Fixture lists - realtime fills the gap between refetches. */
  fixtures: 1000 * 60 * 5,
  /** Live screens lean on Realtime; polling is a fallback only. */
  live: 1000 * 30,
  /** User-owned data (squad, leagues). */
  user: 1000 * 60,
} as const;

export const FANTASY = {
  budget: 100,
  squadSize: 15,
  startingSize: 11,
  /** Minutes before the first kickoff of a round that the deadline falls. */
  deadlineBufferMinutes: 90,
  defaultPriceByPosition: { GK: 4.5, DEF: 4.5, MID: 5.0, FWD: 5.5 } as Record<string, number>,
} as const;

export const SEARCH = {
  debounceMs: 300,
  minQueryLength: 2,
  maxResults: 20,
} as const;

/**
 * Match times + day grouping follow the DEVICE's timezone, so kickoffs show in
 * each user's own local time automatically (no matter the country). Falls back
 * to Cairo only if the OS reports no timezone.
 */
// Wrapped in try/catch because this module sits in the Android widget's headless
// import chain (index.js -> taskHandler -> matchData -> @/config), which runs
// BEFORE the app entry's own guard. A native getCalendars() that ever threw in
// that bare JS context would fail the whole taskHandler module to load -> blank
// widget tile. The try/catch keeps it to a safe Cairo fallback instead.
export const DEFAULT_TIMEZONE: string = (() => {
  try {
    return Localization.getCalendars()[0]?.timeZone ?? 'Africa/Cairo';
  } catch {
    return 'Africa/Cairo';
  }
})();

export const STORAGE_KEYS = {
  locale: 'tashkeela.locale',
  arabicNumerals: 'tashkeela.arabicNumerals',
  /** Set once the first-launch intro/Quick Setup has been seen. */
  seenIntro: 'tashkeela.seenIntro',
  /** Set once the post-onboarding "make your first prediction" nudge has shown. */
  seenFirstPredictNudge: 'tashkeela.seenFirstPredictNudge',
} as const;

/**
 * Supabase env. Static member access is REQUIRED - Expo inlines
 * `process.env.EXPO_PUBLIC_*` at build time only when written literally.
 * Missing env = unconfigured mode (friendly in-app state), never a crash.
 */
function clean(value: string | undefined): string | undefined {
  return value && value.trim().length > 0 ? value.trim() : undefined;
}

export const SUPABASE_URL = clean(process.env.EXPO_PUBLIC_SUPABASE_URL);
export const SUPABASE_ANON_KEY = clean(process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY);
export const isSupabaseConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

/**
 * Google "Web" OAuth client id, used by the NATIVE Google Sign-In on Android so
 * the user signs in via the OS account picker ("Sign in to Tashkeela") instead
 * of the in-app browser routed through the supabase.co domain. When unset, the
 * app safely falls back to the existing web-redirect OAuth flow. Must be the
 * WEB client id (not the Android one) so Google returns an id token.
 */
export const GOOGLE_WEB_CLIENT_ID = clean(process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID);

/**
 * Public showcase demo account. Used ONLY to auto-sign-in visitors inside the
 * web showcase phone frames so they can try every feature without registering.
 * Sharing these credentials is intentional - the account holds only demo data
 * and RLS protects every other user's rows. Seed/refresh it with
 * `npx tsx scripts/seed-demo.ts`.
 */
export const DEMO_ACCOUNT = {
  email: 'demo@tashkeela.app',
  password: 'Demo-Tashkeela-2026!',
} as const;

/** True when the app runs inside the showcase phone frame (`?embed=1`). */
export function isEmbedDemo(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('embed') === '1';
  } catch {
    return false;
  }
}
